import { useRole } from '@/hooks/useRole'
import { ShieldAlert } from '@/components/ui/icons'
import { useNavigate } from 'react-router-dom'

/**
 * Route guard component for role-based access.
 * Wraps page content and shows a graceful "insufficient permissions" UI
 * if the user's role doesn't match.
 *
 * Usage:
 *   <RoleGuard allow={['admin']}>
 *     <KillSwitch />
 *   </RoleGuard>
 *
 *   <RoleGuard allow={['admin', 'operator']}>
 *     <Campaigns />
 *   </RoleGuard>
 */
export function RoleGuard({ allow = [], children }) {
  const { role, hasRole } = useRole()
  const navigate = useNavigate()

  if (hasRole(...allow)) {
    return children
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-(--error)/10 flex items-center justify-center mb-6">
        <ShieldAlert className="w-8 h-8 text-(--error)" />
      </div>
      <h2 className="text-xl font-semibold text-(--foreground) mb-2">
        Insufficient Permissions
      </h2>
      <p className="text-sm text-(--muted-foreground) max-w-md mb-6">
        This page requires <strong>{allow.join(' or ')}</strong> access.
        Your current role is <strong>{role}</strong>.
        Contact an admin if you need elevated permissions.
      </p>
      <button
        onClick={() => navigate('/dashboard')}
        className="px-4 py-2 text-sm font-medium rounded-lg bg-(--accent) text-white hover:opacity-90 transition-opacity"
      >
        Back to Dashboard
      </button>
    </div>
  )
}

/**
 * Utility: hide a UI element if the user lacks the required role.
 * Renders nothing instead of the children.
 *
 * Usage:
 *   <RoleVisible allow={['admin', 'operator']}>
 *     <button>Delete Client</button>
 *   </RoleVisible>
 */
export function RoleVisible({ allow = [], children }) {
  const { hasRole } = useRole()
  if (!hasRole(...allow)) return null
  return children
}
